import { Eye, EyeOff } from 'lucide-react';
import type { RegisterFormData, FormErrors } from '../../../../stores/registerStore';

// Step 4: 密码设置（五步制最后输入步，提交后注册即登录）
export function Step4Password({
  formData,
  errors,
  showPassword,
  showConfirmPassword,
  passwordStrength,
  agreed,
  setAgreed,
  setShowPassword,
  setShowConfirmPassword,
  setFormField,
  validateField,
}: {
  formData: RegisterFormData;
  errors: FormErrors;
  showPassword: boolean;
  showConfirmPassword: boolean;
  passwordStrength: number;
  agreed: boolean;
  setAgreed: (value: boolean) => void;
  setShowPassword: (value: boolean) => void;
  setShowConfirmPassword: (value: boolean) => void;
  setFormField: (field: keyof RegisterFormData, value: string) => void;
  validateField: (field: keyof FormErrors) => boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
        <p className="text-sm text-teal-700">
          最后一步！设置登录密码后即可完成注册并直接进入系统
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">密码</label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => {
              setFormField('password', e.target.value);
              validateField('password');
              validateField('confirmPassword');
            }}
            onBlur={() => validateField('password')}
            className={`w-full px-4 py-3 pr-10 border rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 transition-colors ${
              errors.password ? 'border-red-300 focus:border-red-400 bg-red-50' : 'bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500'
            }`}
            placeholder="请设置8-20位密码，包含字母与数字"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showPassword ? <EyeOff className="h-5 w-5 text-slate-400" /> : <Eye className="h-5 w-5 text-slate-400" />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}

        {/* 密码强度指示器 */}
        <div className="mt-2">
          <div className="flex justify-between mb-1">
            <span className="text-xs text-slate-500">密码强度</span>
            <span className="text-xs font-medium">
              {passwordStrength < 33 ? '弱' : passwordStrength < 66 ? '中' : '强'}
            </span>
          </div>
          <div className="w-full h-2 flex gap-1">
            {[1, 2, 3].map((level) => (
              <div
                key={level}
                className={`flex-1 rounded-full transition-colors ${
                  level <= passwordStrength / 33 ?
                    level === 1 ? 'bg-red-500' :
                    level === 2 ? 'bg-amber-500' : 'bg-green-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">确认密码</label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormField('confirmPassword', e.target.value);
              validateField('confirmPassword');
            }}
            onBlur={() => validateField('confirmPassword')}
            className={`w-full px-4 py-3 pr-10 border rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 transition-colors ${
              errors.confirmPassword ? 'border-red-300 focus:border-red-400 bg-red-50' : 'bg-slate-50 border-slate-200 focus:bg-white focus:border-teal-500'
            }`}
            placeholder="请确认密码"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showConfirmPassword ? <EyeOff className="h-5 w-5 text-slate-400" /> : <Eye className="h-5 w-5 text-slate-400" />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
      </div>

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="agree-policy"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-teal-500 rounded border-slate-300"
        />
        <label htmlFor="agree-policy" className="text-sm text-slate-600 cursor-pointer hover:text-slate-800 transition-colors">
          我已阅读并同意《用户协议》和《隐私政策》
        </label>
      </div>
    </div>
  );
}